/**
 * useVisibleTableIds
 *
 * The AR tables a form type's layout renders in at least one of the given
 * visibility modes, by `tableId`. `useXlsxAttachment` reads it for the single
 * mode a workbook exports at; `WorkflowParameterContent` reads it for the union
 * of the modes a send renders at, because the PDF's export view and the XLSX's
 * differ whenever the send omits its own PDF and a `savePdfToForm` action
 * carries a different one (SYD-2412).
 *
 * A mode of `undefined` matches every table, mirroring `elementVisibility`. An
 * empty `visibilityModes` therefore yields no table at all rather than all of
 * them — a send that builds neither document renders nothing.
 *
 * `visibilityModes` is a memo dependency, so a caller that rebuilds the array
 * every render recomputes every render.
 */

import { useMemo } from 'react';
import { useSnapshot } from 'valtio';
import type { UIElement, VisibilityTypeId } from '#types/System.types';
import { formStore } from '@/store';
import { elementVisibility } from '@/components/rendering/elementVisibility';
import discoverArTablesInLayoutStructure from '@/components/modals/FormEmailModal/create/discoverArTablesInLayoutStructure';

export const useVisibleTableIds = (
  visibilityModes: readonly (VisibilityTypeId | undefined)[],
): string[] => {
  const formTypeSnap = useSnapshot(formStore.state.formType);
  const layoutStructure = formTypeSnap.data?.defaultUILayout?.structure as UIElement | undefined;

  return useMemo(() => {
    if (!layoutStructure) return [];
    return discoverArTablesInLayoutStructure(layoutStructure)
      .filter((table) => visibilityModes.some((mode) => elementVisibility(table, mode)))
      .map((table) => table.tableId);
  }, [layoutStructure, visibilityModes]);
};
